import { Router, ActivatedRoute } from '@angular/router';
import { Observable, Subject, timer, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import {
  Output,
  EventEmitter,
  Component,
  OnInit,
  ViewChild,
  OnDestroy,
  Input,
} from '@angular/core';
import { ConfirmationService, LazyLoadEvent, SelectItem, Table } from 'primeng';
import { IPageResult } from '../../interfaces/page-result';
import { ISupplierBatch } from '../../interfaces/supplier-batch';
import { ISupplierBatchFilter } from '../../interfaces/supplier-batch-filter';
import { BillingService } from '../../services';
import { SelfBillingAgreementService } from '../../services';
import { SettingsService } from '../../services';
import { SupplierBatchService } from '../../services';
import { SupplierInvoiceService } from '../../services';
import { flatten } from '../../utils/flatten';
import { MessageService } from 'primeng';
import { Message } from '../../classes/Message';
import { InvoiceProcessMode } from '../../classes/invoice-process-mode';
import { SystemSetting } from '../settings/settings';
import { map, tap } from 'rxjs/operators';
import { SuppliersInvoicesGridComponent } from './suppliers-invoices-grid.component';
import { IXeroTokenRecord } from '../../interfaces/xero-token-record';

enum SupplierInvoiceProcessType {
  SupplierInvoice = 1,
  SupplierBatch = 2,
  SupplierPayment = 3,
  SupplierXeroBatches = 4,
}

enum PaymentProcessStatus {
  Completed = 1,
  ValidationError = 2,
  RevolutError = 3,
  OtherError = 4,
}

@Component({
  selector: 'suppliers-batches',
  templateUrl: './suppliers-batches.component.html',
})
//@SupplierInvoiceProcessingTypeEnumAware
export class SuppliersBatchesComponent implements OnInit, OnDestroy {
  @ViewChild('invoicesGrid')
  invoicesGrid: SuppliersInvoicesGridComponent;

  public page: IPageResult<ISupplierBatch> = {
    items: [],
    total: 0,
  };
  public isLoaded: boolean = false;
  public filterVal: ISupplierBatchFilter;
  public filter: Subject<ISupplierBatchFilter>;
  public selectedBatch = null;
  private processPaymentSubscription: Subscription;
  private processInvoiceSubscription: Subscription;
  public hideInvoicesTillBatchesUpdate: boolean = false;

  //testing
  public isTestMode: boolean;
  public isTest2Mode: boolean;
  public plannedRunDay: Date = new Date();
  public emulateToday: Date = new Date();
  public runDelayedDays: number = 0;

  public supplierInvoiceProcess: InvoiceProcessMode;
  public supplierBatchProcess: InvoiceProcessMode;
  public supplierPaymentProcess: InvoiceProcessMode;
  public supplierXeroBatchProcess: InvoiceProcessMode;
  public supplierProcessInvoiceTypes;
  public transactionsFrom: Date = new Date();
  public transactionsDays: number = 1;
  public transactionsSupplierId: number;
  public selfBillingsupplierId: number;
  public transactionsSuppliers: SelectItem[] = [];

  public billResults: any[];
  public emailResults: any[];

  public batchesPlannedRunDay: Date = new Date();
  public paymentPlannedRunDay: Date = new Date();
  public xeroBatchesPlannedRunDay: Date = new Date();
  public batchesRunDelayedDays: number = 0;
  public generateBatchResults: any[];
  public payInvoicesResults: any[];
  public xeroResults: any[];
  public invoiceBillingColumns: any[];
  public showProgressBar: boolean;
  public progressBarTitle: string;
  public xeroTokenRecord: IXeroTokenRecord;

  private _invoiceSuppliersSelectList: SelectItem[];

  @Input('invoiceSuppliers')
  set suppliersSelectList(value: SelectItem[]) {
    this._invoiceSuppliersSelectList = value;
    this.transactionsSuppliers = value;
  }

  get suppliersSelectList(): SelectItem[] {
    return this._invoiceSuppliersSelectList;
  }

  //

  @Output() onGridAction: EventEmitter<any>;

  constructor(
    private supplierInvoiceService: SupplierInvoiceService,
    private billingService: BillingService,
    private selfBillingAgreementService: SelfBillingAgreementService,
    private supplierBatchService: SupplierBatchService,
    public messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private settingsService: SettingsService,
    private confirmationService: ConfirmationService,
  ) {
    this.onGridAction = new EventEmitter<any>();
    this.supplierInvoiceProcess = new InvoiceProcessMode();
    this.supplierBatchProcess = new InvoiceProcessMode();
    this.supplierPaymentProcess = new InvoiceProcessMode();
    this.supplierXeroBatchProcess = new InvoiceProcessMode();
    this.supplierProcessInvoiceTypes = SupplierInvoiceProcessType;
  }

  ngOnInit() {
    this.isTestMode = this.route.snapshot.params['test'] == '1';
    this.isTest2Mode = this.route.snapshot.params['test'] == '2';

    this.initDefaultFilter();

    var datesRange = this.billingService.getLastMonthRange();
    this.filterVal.filters.from = { value: datesRange.from };
    this.filterVal.filters.to = { value: datesRange.to };
    this.filterVal.filters.awaitingVerification = { value: true };

    if (this.isTest2Mode) this.filterVal.filters.awaitingVerification = { value: false };

    this.filter = new Subject<ISupplierBatchFilter>();

    // subscribe to filter changes with a debounce time
    this.filter.pipe(debounceTime(500)).subscribe((f) => {
      this.load(f);
      this.filterVal = f;
    });

    // set default filter
    this.filter.next(this.filterVal);

    //---- testing ----

    // get system settings, limit to the specific id, but we may need to have diff pass phrase for diff job, we may need to pass in an array
    this.settingsService.GetInvoiceProcessPassPhrase(2).then((data: SystemSetting) => {
      if (data)
        this.supplierXeroBatchProcess.systemInfo.passPhrase =
          this.supplierInvoiceProcess.systemInfo.passPhrase =
          this.supplierBatchProcess.systemInfo.passPhrase =
          this.supplierPaymentProcess.systemInfo.passPhrase =
            data.value;
    });

    this.setInvoiceBillingColumns();
  }

  private setInvoiceBillingColumns() {
    this.invoiceBillingColumns = [
      { field: 'supplierName', header: 'Supplier' },
      { field: 'isSelfBilled', header: 'Self Billed' },
      { field: 'selfBillingValidity', header: 'Self Billing Validity' },
      { field: 'statusString', header: 'Status' },
      { field: 'statusDetails', header: 'Status Details' },
      { field: 'amount', header: 'Total Amount' },
      { field: 'transactionsLinked', header: 'Transactions' },
    ];
  }

  initDefaultFilter() {
    this.filterVal = {
      first: 0,
      rows: 10,
      sortField: 'RunOn',
      sortOrder: 1,
      filters: {
        awaitingVerification: { value: null },
        from: { value: null },
        to: { value: null },
      },
    };
  }

  load(filter: ISupplierBatchFilter) {
    this.supplierBatchService.getPage(flatten(filter)).then((result) => {
      this.page.items = result.items;
      this.page.total = result.total;
      this.isLoaded = true;

      if (this.isTest2Mode) {
        if (this.selectedBatch == null && this.page.items.length)
          this.selectedBatch = this.page.items[0];
      }

      this.hideInvoicesTillBatchesUpdate = false;

      //recovering item's selection if its field(s) has been changed
      if (this.selectedBatch != null) {
        var foundPageItem = this.page.items.find((o) => o.id == this.selectedBatch.id);

        if (foundPageItem != null) this.selectedBatch = foundPageItem;
      }
      //
    });
  }

  onGridLazyLoad(event: LazyLoadEvent) {
    this.filterVal.first = event.first;
    this.filterVal.rows = event.rows;
    this.filterVal.sortField = event.sortField;
    this.filterVal.sortOrder = event.sortOrder;
    // map grid lazy load event to filter
    this.filter.next(this.filterVal);
  }

  clearFilter(dt: Table) {
    dt.sortField = '';
    dt.rows = 10;
    this.initDefaultFilter();
    this.filter.next(this.filterVal);

    this.selectedBatch = null;
  }

  search() {
    this.filterVal.first = 0; //fix error - when you set page > 1 and filter, then data not display
    this.filter.next(this.filterVal);

    this.selectedBatch = null;
  }

  //update the items grid with the same filter/paging/sorting settings
  private gridReload() {
    this.load(this.filterVal);
  }

  public gridActionUpdate(actionId: number) {
    this.gridReload();

    if (this.selectedBatch) {
      this.invoicesGrid.gridActionUpdate(actionId);
    }
  }

  onBatchSelect(event) {
    if (this.invoicesGrid) this.invoicesGrid.deselectInvoice();
  }

  confirmBatch(id) {
    //if awaiting verification filter is set then on batches update the selected record will be hidden
    //and so we don't allow to show invoices grid cause it would flicker
    this.hideInvoicesTillBatchesUpdate = this.filterVal.filters.awaitingVerification.value;

    this.supplierBatchService
      .confirmBatch(id)
      .then(() => {
        this.messageService.add(Message.Success(`Batch confirmed`, 'Confirm Batch'));
        this.search();
      })
      .catch(() => {
        this.messageService.add(Message.Error(`Error confirming batch`, 'Confirm Batch'));
      });
  }

  onSupplierInvoicesGridAction(actionId: number) {
    this.onGridAction.emit(actionId);
  }

  processInvoices() {
    this.billResults = null;
    this.emailResults = null;
    this.enableProgressBar('Processing Invoice');
    this.processInvoiceSubscription = this.billingService
      .processSupplierInvoices(
        this.plannedRunDay,
        this.runDelayedDays,
        this.supplierInvoiceProcess.isDryRun,
      )
      .pipe(
        map((res) => JSON.parse(res)),
        tap({
          next: ({ billResults }) => {
            this.messageService.add(Message.Success('Supplier invoices processed'));
            this.supplierInvoiceProcess.isDryRun = true;
            this.hideProgressBar();
            this.search();
          },
          error: (err) => {
            this.hideProgressBar();
            console.log(err);
            this.messageService.add(
              Message.Error(`Error processing supplier invoice`, 'Supplier Invoice process Error'),
            ); // apparently server side events do not return the text/ message of the error :(
            this.supplierInvoiceProcess.isDryRun = true;
          },
        }),
      )
      .subscribe(({ billResults, emailResults }) => {
        this.billResults = billResults || null;
        this.emailResults = emailResults || null;
      });
  }

  removeInvoices() {
    this.billingService.removeSupplierInvoices().then(() => {
      this.messageService.add(Message.Success(`Done`, ''));
      this.search();
    });
  }

  generateTransactions() {
    this.billingService
      .generateSupplierTransactions(
        this.transactionsSupplierId,
        this.transactionsFrom,
        this.transactionsDays,
      )
      .then((res) => {
        this.messageService.add(
          Message.Success(`Records created: ${res}`, 'Generate transactions'),
        );
      })
      .catch(() => {
        this.messageService.add(Message.Error('', `Generate transactions error.`));
      });
  }

  processBatches() {
    this.generateBatchResults = null;
    this.payInvoicesResults = null;
    this.enableProgressBar('Processing Batches');
    this.billingService
      .processBatches(
        this.batchesPlannedRunDay,
        this.batchesRunDelayedDays,
        this.supplierBatchProcess.isDryRun,
      )
      .then((res) => {
        this.generateBatchResults = res.generateResult.batchResults;
        this.supplierBatchProcess.isDryRun = true;
        console.log('BATCHES PROCESS RESULT: ', res);
        this.messageService.add(Message.Success(`Batches processed`, 'Batches processed'));
        this.supplierBatchProcess.isDryRun = true;
        this.hideProgressBar();
        this.search();
      })
      .catch((res) => {
        this.hideProgressBar();
        this.messageService.add(Message.Error(res));
        this.supplierBatchProcess.isDryRun = true;
      });
  }

  processPayments() {
    this.payInvoicesResults = null;
    this.enableProgressBar('Processing Payments');
    this.processPaymentSubscription = this.billingService
      .processPayments(
        this.paymentPlannedRunDay,
        this.batchesRunDelayedDays,
        this.supplierPaymentProcess.isDryRun,
      )
      .pipe(
        map((res) => JSON.parse(res)),
        tap({
          next: ({ payResult }) => {
            if (payResult && payResult.status == PaymentProcessStatus.Completed) {
              this.messageService.add(Message.Success(`Payments processed`, 'Payments processed'));
            } else {
              this.messageService.add(
                Message.Error(`Error processing payment request`, 'Payments process Error'),
              );
            }

            this.supplierPaymentProcess.isDryRun = true;
            this.hideProgressBar();
            this.search();
          },
          error: (err) => {
            this.hideProgressBar();
            console.log(err);
            this.messageService.add(
              Message.Error(`Error processing payment request`, 'Payments process Error'),
            ); // apparently server side events do not return the text/ message of the error :(
            this.supplierPaymentProcess.isDryRun = true;
          },
        }),
      )
      .subscribe(({ payResult, emailResults }) => {
        this.payInvoicesResults = (payResult && payResult.invoiceResults) || null;
        this.emailResults = emailResults || null;
      });
  }

  processXeroBatches() {
    this.payInvoicesResults = null;
    this.enableProgressBar('Processing Xero Batches');
    this.billingService
      .processXeroBatches(
        this.xeroBatchesPlannedRunDay,
        this.batchesRunDelayedDays,
        this.supplierXeroBatchProcess.isDryRun,
      )
      .then((res) => {
        this.supplierXeroBatchProcess.isDryRun = true;
        if (res.xeroResult) this.xeroResults = res.xeroResult.batchResults;
        this.messageService.add(
          Message.Success(`Xero Batches processed`, 'Xero Batches processed'),
        );
        this.supplierXeroBatchProcess.isDryRun = true;
        this.hideProgressBar();
        this.search();
      })
      .catch((res) => {
        this.hideProgressBar();
        this.messageService.add(Message.Error(res));
        this.supplierXeroBatchProcess.isDryRun = true;
      });
  }

  confirmInvoiceProcess(message: string, callbackFunction: Function) {
    this.confirmationService.confirm({
      message: message,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      key: 'supplier-invoice-dialog',
      accept: () => {
        if (callbackFunction) callbackFunction();
      },
      reject: () => {},
    });
  }

  verifyRunMode($this, runType) {
    var $thisInputText = $this.target.value;
    $thisInputText =
      $thisInputText != '' && $thisInputText != null ? $thisInputText.trim() : $thisInputText;
    var verificationCheck: string;
    // modify the value of run mode on the form
    if (runType == SupplierInvoiceProcessType.SupplierInvoice) {
      verificationCheck = this.supplierInvoiceProcess.systemInfo.passPhrase;
      if ($thisInputText === verificationCheck) {
        // incase we wish to have diff pass phrases
        this.confirmInvoiceProcess(this.supplierInvoiceProcess.invoiceWetRunMessage, () => {
          this.supplierInvoiceProcess.isDryRun = !this.supplierInvoiceProcess.isDryRun;
        });
        $this.target.value = '';
      }
    } else if (runType == SupplierInvoiceProcessType.SupplierBatch) {
      verificationCheck = this.supplierBatchProcess.systemInfo.passPhrase;
      if ($thisInputText === verificationCheck) {
        this.confirmInvoiceProcess(this.supplierBatchProcess.batchWetRunMessage, () => {
          this.supplierBatchProcess.isDryRun = !this.supplierBatchProcess.isDryRun;
        });
        $this.target.value = '';
      }
    } else if (runType == SupplierInvoiceProcessType.SupplierPayment) {
      verificationCheck = this.supplierPaymentProcess.systemInfo.passPhrase;
      if ($thisInputText === verificationCheck) {
        this.confirmInvoiceProcess(this.supplierPaymentProcess.paymentWetRunMessage, () => {
          this.supplierPaymentProcess.isDryRun = !this.supplierPaymentProcess.isDryRun;
        });
        $this.target.value = '';
      }
    } else if (runType == SupplierInvoiceProcessType.SupplierXeroBatches) {
      verificationCheck = this.supplierXeroBatchProcess.systemInfo.passPhrase;
      if ($thisInputText === verificationCheck) {
        this.confirmInvoiceProcess(this.supplierXeroBatchProcess.xeroBatchWetRunMessage, () => {
          this.supplierXeroBatchProcess.isDryRun = !this.supplierXeroBatchProcess.isDryRun;
        });
        $this.target.value = '';
      }
    }
  }

  removeBatch(id) {
    this.billingService.removeBatch(id).then(() => {
      this.messageService.add(Message.Success(`Done`, ''));
      this.search();
    });
  }

  removeBatches() {
    this.billingService.removeBatches().then(() => {
      this.messageService.add(Message.Success(`Done`, ''));
      this.search();
    });
  }

  //----------------

  runAll() {
    this.billingService
      .processAll(this.emulateToday)
      .then((res) => {
        this.messageService.add(Message.Success(`Run All completed`, 'Run All completed'));
      })
      .catch(() => {
        //this.messageService.add(Message.Error('', `Run All error.`));
      });
  }

  removeXeroData() {
    this.billingService.removeXeroData().then(() => {
      this.messageService.add(Message.Success(`Done`, ''));
      this.search();
    });
  }

  enableProgressBar(title: string) {
    this.progressBarTitle = title;
    this.showProgressBar = true;
  }

  hideProgressBar() {
    this.showProgressBar = false;
  }

  ngOnDestroy() {
    if (this.processPaymentSubscription) {
      this.processPaymentSubscription.unsubscribe();
    }
    if (this.processInvoiceSubscription) {
      this.processInvoiceSubscription.unsubscribe();
    }
  }
}
